import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import {
    Alert,
    Badge,
    Button,
    Card,
    CardBody,
    CardHeader,
    Col,
    Container,
    FormGroup,
    Input,
    Label,
    Row,
    Spinner,
    Table
} from 'reactstrap';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import { FaFileInvoice, FaMoneyBillWave, FaTruck } from 'react-icons/fa';
import Select from 'react-select';
import { useLocation, useNavigate } from 'react-router-dom';
import { supplierActions } from '../../sagas/supplierSlice';
import { reglementsFournisseurActions } from '../../sagas/reglementFournisseurSlice';
import { formatPrice, preventScroll } from '../../helpers/utility';
import invoiceSupplierService from '../../services/invoiceSupplierService';
import * as receiptNoteService from '../../services/receiptNoteService';
import retourFournisseurService from '../../services/retourFournisseurService';
import './ReglementForm.css';

const unwrapList = (response) => {
    const data = response?.data ?? response;
    const body = data?.body ?? data;
    return Array.isArray(body) ? body : (body?.content || body?.data || []);
};

const toNumber = (value) => Number.isFinite(Number(value)) ? Number(value) : 0;

const ReglementFournisseurForm = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation();
    const { loading } = useSelector(state => state.reglementsFournisseur);
    const { suppliers } = useSelector(state => state.suppliers);
    const reglement = location.state?.reglement || null;
    const canEdit = (reglement && reglement.statut === 'EN_ATTENTE') || !reglement;

    const [selectedFournisseur, setSelectedFournisseur] = useState(null);
    const [typeReglement, setTypeReglement] = useState(reglement?.typeReglement || 'FACTURE');
    const [selectedDocuments, setSelectedDocuments] = useState([]);
    const [documents, setDocuments] = useState([]);
    const [documentsLoading, setDocumentsLoading] = useState(false);
    const [availableRetours, setAvailableRetours] = useState([]);
    const [selectedRetours, setSelectedRetours] = useState(reglement?.utilisationRetours || []);
    const [loadingRetours, setLoadingRetours] = useState(false);
    const formikRef = useRef(null);

    const fournisseurOptions = useMemo(() => (suppliers || []).map(fournisseur => ({
        value: fournisseur.id,
        label: fournisseur.typeFournisseur === 'PARTICULIER'
            ? `${fournisseur.nom || ''} ${fournisseur.prenom || ''} (${fournisseur.code || ''})`.trim()
            : `${fournisseur.entreprise || ''} (${fournisseur.code || ''})`,
        solde: fournisseur.solde,
        totalDebit: fournisseur.totalDebit,
        totalCredit: fournisseur.totalCredit,
    })), [suppliers]);

    const typeReglementOptions = [
        { value: 'FACTURE', label: t('reglement.factures'), icon: FaFileInvoice },
        { value: 'BON_RECEPTION', label: t('reglement.boReceptions'), icon: FaTruck },
    ];

    const modeReglementOptions = [
        { value: 'ESPECES', label: t('reglement.especes') },
        { value: 'CHEQUE', label: t('reglement.cheque') },
        { value: 'VIREMENT', label: t('reglement.virement') },
        { value: 'CARTE_BANCAIRE', label: t('reglement.carteBancaire') },
    ];

    const initialValues = {
        id: reglement?.id || null,
        reference: reglement?.reference || '',
        fournisseurId: reglement?.fournisseurId || '',
        numFactures: reglement?.numFactures || '',
        numBonReceptions: reglement?.numBonReceptions || '',
        typeReglement: reglement?.typeReglement || 'FACTURE',
        montantRegle: reglement?.montantRegle || 0,
        montantPaiementReel: reglement?.montantPaiementReel || reglement?.montantRegle || 0,
        montantRetoursUtilises: reglement?.montantRetoursUtilises || 0,
        dateReglement: reglement?.dateReglement || new Date().toLocaleDateString('en-CA', { timeZone: 'Africa/Casablanca' }),
        statut: reglement?.statut || 'EN_ATTENTE',
        statutPrecedent: reglement?.statutPrecedent || null,
        commentaire: reglement?.commentaire || '',
        modeReglement: reglement?.modeReglement || 'ESPECES',
        referencePaiement: reglement?.referencePaiement || '',
        numeroCheque: reglement?.numeroCheque || '',
        banque: reglement?.banque || '',
        dateEcheance: reglement?.dateEcheance || '',
    };

    const validationSchema = Yup.object().shape({
        fournisseurId: Yup.number().required(t('reglement.fournisseurRequired')),
        dateReglement: Yup.date().required(t('reglement.dateReglementRequired')),
        modeReglement: Yup.string().required(t('reglement.modeReglementRequired')),
    });

    const getDocumentFournisseurId = (doc) => doc.fournisseurId || doc.fournisseur?.id;
    const getDocumentFournisseurName = (doc) => doc.fournisseurNom || doc.nomFournisseur || doc.fournisseurName || doc.fournisseur?.entreprise || '-';
    const getDocumentDate = (doc) => doc.dateFacture || doc.dateReception || doc.dateOperation || doc.createdAt || '-';
    const getDocumentTotal = (doc) => toNumber(doc.montantTotalTTC ?? doc.montantTTC ?? doc.totalTTC);
    const getDocumentPaid = (doc) => toNumber(doc.montantRegle ?? doc.montantPaye ?? doc.montantPayeTTC);
    const getDocumentRemaining = (doc) => Math.max(toNumber(doc.montantRestant ?? (getDocumentTotal(doc) - getDocumentPaid(doc))), 0);

    const selectedTotal = useMemo(
        () => selectedDocuments.reduce((sum, doc) => sum + getDocumentRemaining(doc), 0),
        [selectedDocuments]
    );
    const retoursTotal = useMemo(
        () => selectedRetours.reduce((sum, retour) => sum + toNumber(retour.montantUtilise), 0),
        [selectedRetours]
    );
    const amountToPay = Math.max(selectedTotal - retoursTotal, 0);

    const filteredDocuments = useMemo(() => {
        return documents
            .filter(doc => getDocumentRemaining(doc) > 0)
            .filter(doc => !selectedFournisseur || String(getDocumentFournisseurId(doc)) === String(selectedFournisseur.value));
    }, [documents, selectedFournisseur]);

    const applyReturns = (total, retours, setFieldValue) => {
        let remaining = total;
        const next = retours
            .filter(retour => !retour.statut || retour.statut === 'VALIDE')
            .map(retour => {
                const available = toNumber(retour.montantDisponible ?? retour.montantTTC);
                const montantUtilise = Math.min(available, remaining);
                remaining = Math.max(remaining - montantUtilise, 0);
                return {
                    retourId: retour.id,
                    numeroRetour: retour.numeroRetour,
                    montantRetour: retour.montantTTC,
                    montantDisponible: available,
                    montantUtilise,
                    dateRetour: retour.dateOperation,
                };
            })
            .filter(retour => retour.montantUtilise > 0);

        const used = next.reduce((sum, retour) => sum + retour.montantUtilise, 0);
        setSelectedRetours(next);
        setFieldValue('montantRegle', total);
        setFieldValue('montantRetoursUtilises', used);
        setFieldValue('montantPaiementReel', Math.max(total - used, 0));
    };

    const reloadDocuments = () => {
        setDocumentsLoading(true);
        const request = typeReglement === 'FACTURE'
            ? invoiceSupplierService.search({ fournisseurId: selectedFournisseur?.value })
            : receiptNoteService.search({ fournisseurId: selectedFournisseur?.value, paymentStatus: 'IMPAYE' });

        Promise.resolve(request)
            .then(response => setDocuments(unwrapList(response)))
            .catch(() => setDocuments([]))
            .finally(() => setDocumentsLoading(false));
    };

    useEffect(() => {
        dispatch(supplierActions.search({ query: '' }));
    }, [dispatch]);

    useEffect(() => {
        if (!reglement || suppliers.length === 0) return;
        const fournisseur = fournisseurOptions.find(option => String(option.value) === String(reglement.fournisseurId));
        if (fournisseur) setSelectedFournisseur(fournisseur);
    }, [reglement, suppliers, fournisseurOptions]);

    useEffect(() => {
        reloadDocuments();
    }, [typeReglement, selectedFournisseur?.value]);

    useEffect(() => {
        if (!selectedFournisseur) {
            setAvailableRetours([]);
            setSelectedRetours([]);
            return;
        }

        setLoadingRetours(true);
        retourFournisseurService.availableCredits(selectedFournisseur.value)
            .then(({ data }) => setAvailableRetours((data || []).filter(retour => !retour.statut || retour.statut === 'VALIDE')))
            .catch(() => setAvailableRetours([]))
            .finally(() => setLoadingRetours(false));
    }, [selectedFournisseur?.value]);

    useEffect(() => {
        if (selectedDocuments.length === 0 || !formikRef.current) return;
        applyReturns(selectedTotal, availableRetours, formikRef.current.setFieldValue);
    }, [availableRetours, selectedTotal, selectedDocuments.length]);

    const selectFournisseurFromDocument = (doc, setFieldValue) => {
        const fournisseurId = getDocumentFournisseurId(doc);
        const option = fournisseurOptions.find(item => String(item.value) === String(fournisseurId));
        if (option && !selectedFournisseur) {
            setSelectedFournisseur(option);
            setFieldValue('fournisseurId', option.value);
        }
        return option;
    };

    const toggleDocument = (doc, values, setFieldValue) => {
        const exists = selectedDocuments.some(item => item.id === doc.id);
        const next = exists
            ? selectedDocuments.filter(item => item.id !== doc.id)
            : [...selectedDocuments, doc];

        if (!exists && selectedDocuments.length > 0) {
            const currentFournisseurId = getDocumentFournisseurId(selectedDocuments[0]);
            if (String(getDocumentFournisseurId(doc)) !== String(currentFournisseurId)) {
                Swal.fire({
                    icon: 'warning',
                    title: t('common.warning', 'Attention'),
                    text: t('reglement.sameFournisseurRequired', 'Veuillez sélectionner des documents du même fournisseur.'),
                });
                return;
            }
        }

        const selectedOption = next.length > 0 ? selectFournisseurFromDocument(next[0], setFieldValue) : selectedFournisseur;
        if (next.length === 0 && !reglement) {
            setSelectedFournisseur(null);
            setFieldValue('fournisseurId', '');
        } else if (selectedOption) {
            setFieldValue('fournisseurId', selectedOption.value);
        }

        const total = next.reduce((sum, item) => sum + getDocumentRemaining(item), 0);
        setSelectedDocuments(next);
        applyReturns(total, availableRetours, setFieldValue);
    };

    const handleFournisseurFilterChange = (option, setFieldValue) => {
        setSelectedFournisseur(option);
        setSelectedDocuments([]);
        setSelectedRetours([]);
        setFieldValue('fournisseurId', option?.value || '');
        setFieldValue('montantRegle', 0);
        setFieldValue('montantRetoursUtilises', 0);
        setFieldValue('montantPaiementReel', 0);
    };

    const handleSubmit = (values, { setSubmitting }) => {
        if (selectedDocuments.length === 0) {
            Swal.fire({
                icon: 'warning',
                title: t('common.warning', 'Attention'),
                text: t('reglement.documentsRequired', 'Veuillez sélectionner au moins un document à régler.'),
            });
            setSubmitting(false);
            return;
        }

        const selectedFactures = typeReglement === 'FACTURE' ? selectedDocuments.map(doc => doc.numero).join(',') : '';
        const selectedBonReceptions = typeReglement === 'BON_RECEPTION' ? selectedDocuments.map(doc => doc.numero).join(',') : '';
        const payload = {
            data: {
                ...values,
                typeReglement,
                numFactures: selectedFactures,
                numBonReceptions: selectedBonReceptions,
                montantRegle: selectedTotal,
                montantRetoursUtilises: retoursTotal,
                montantPaiementReel: amountToPay,
                documentsIds: selectedDocuments.map(doc => doc.id),
                estAvance: false,
                utilisationAvances: [],
                utilisationRetours: selectedRetours.map(retour => ({
                    retourId: retour.retourId,
                    numeroRetour: retour.numeroRetour,
                    montantUtilise: toNumber(retour.montantUtilise),
                    commentaire: retour.commentaire || ''
                })),
            },
            onSuccess: () => {
                Swal.fire({ icon: 'success', title: t('common.success'), text: reglement ? t('reglement.updateSuccess') : t('reglement.createSuccess') });
                navigate('/purchases-module/payments/fournisseur');
            },
            onError: (error) => {
                Swal.fire({ icon: 'error', title: t('common.error'), text: error.message || t('common.errorOccurred') });
                setSubmitting(false);
            }
        };

        if (reglement) {
            dispatch(reglementsFournisseurActions.updateRequest({ ...payload, id: reglement.id }));
        } else {
            dispatch(reglementsFournisseurActions.createRequest(payload));
        }
    };

    const renderDocuments = (values, setFieldValue) => (
        <Card className="h-100 shadow-sm">
            <CardHeader className="bg-white">
                <div className="d-flex flex-wrap gap-2 justify-content-between align-items-center">
                    <h5 className="mb-0">{t('reglement.documentsARegler')}</h5>
                    <Badge color="primary">{selectedDocuments.length} {t('reglement.documentsSelectionnes')}</Badge>
                </div>
            </CardHeader>
            <CardBody>
                <Row className="g-2 mb-3">
                    <Col md={7}>
                        <Select
                            options={fournisseurOptions}
                            value={selectedFournisseur}
                            onChange={(option) => handleFournisseurFilterChange(option, setFieldValue)}
                            placeholder={t('reglement.selectFournisseur')}
                            isClearable={!reglement}
                            isDisabled={!canEdit}
                            menuPortalTarget={typeof window !== 'undefined' ? document.body : null}
                            styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                        />
                        <ErrorMessage name="fournisseurId" component="div" className="text-danger small mt-1" />
                    </Col>
                    <Col md={5}>
                        <Select
                            options={typeReglementOptions}
                            value={typeReglementOptions.find(option => option.value === typeReglement)}
                            onChange={(option) => {
                                setTypeReglement(option.value);
                                setSelectedDocuments([]);
                                setSelectedRetours([]);
                                setFieldValue('typeReglement', option.value);
                                setFieldValue('montantRegle', 0);
                                setFieldValue('montantRetoursUtilises', 0);
                                setFieldValue('montantPaiementReel', 0);
                            }}
                            isDisabled={!canEdit}
                            menuPortalTarget={typeof window !== 'undefined' ? document.body : null}
                            styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                            formatOptionLabel={(option) => (
                                <div className="d-flex align-items-center">
                                    <option.icon className="me-2" />
                                    {option.label}
                                </div>
                            )}
                        />
                    </Col>
                </Row>

                {documentsLoading ? (
                    <div className="text-muted"><Spinner size="sm" className="me-2" />{t('common.loading', 'Chargement...')}</div>
                ) : (
                    <div className="table-responsive" style={{ maxHeight: 560, overflowY: 'auto' }}>
                        <Table hover size="sm" className="align-middle">
                            <thead className="table-light" style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                                <tr>
                                    <th style={{ width: 36 }}></th>
                                    <th>{t('reglement.documentNumber', 'N°')}</th>
                                    <th>{t('reglement.fournisseur')}</th>
                                    <th>{t('reglement.date')}</th>
                                    <th className="text-end">{t('reglement.montantRestant')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredDocuments.map(doc => {
                                    const checked = selectedDocuments.some(item => item.id === doc.id);
                                    return (
                                        <tr key={`${typeReglement}-${doc.id}`} onClick={() => canEdit && toggleDocument(doc, values, setFieldValue)} style={{ cursor: canEdit ? 'pointer' : 'default' }}>
                                            <td>
                                                <Input type="checkbox" checked={checked} readOnly />
                                            </td>
                                            <td className="fw-semibold">{doc.numero}</td>
                                            <td>{getDocumentFournisseurName(doc)}</td>
                                            <td>{getDocumentDate(doc)}</td>
                                            <td className="text-end fw-bold text-danger">{formatPrice(getDocumentRemaining(doc))}</td>
                                        </tr>
                                    );
                                })}
                                {filteredDocuments.length === 0 && (
                                    <tr>
                                        <td colSpan="5" className="text-center text-muted py-4">{t('reglement.noDocuments', 'Aucun document impayé')}</td>
                                    </tr>
                                )}
                            </tbody>
                        </Table>
                    </div>
                )}
            </CardBody>
        </Card>
    );

    const renderPayment = (values, setFieldValue, isSubmitting, errors, touched, handleChange, handleBlur) => (
        <Card className="h-100 shadow-sm">
            <CardHeader className="bg-white">
                <h5 className="mb-0"><FaMoneyBillWave className="me-2" />{t('reglement.detailsReglement')}</h5>
            </CardHeader>
            <CardBody>
                <Alert color="light">
                    <Row className="g-2">
                        <Col xs={4}>{t('reglement.totalDocuments', 'Total documents')}<br /><strong>{formatPrice(selectedTotal)}</strong></Col>
                        <Col xs={4}>{t('reglement.retoursUtilises', 'Avoirs utilisés')}<br /><strong className="text-success">{formatPrice(retoursTotal)}</strong></Col>
                        <Col xs={4}>{t('reglement.montantAPayer', 'À payer')}<br /><strong className="text-primary">{formatPrice(amountToPay)}</strong></Col>
                    </Row>
                </Alert>

                <Row>
                    <Col md={6}>
                        <FormGroup>
                            <Label>{t('reglement.dateReglement')} *</Label>
                            <Field name="dateReglement" type="date" className={`form-control ${errors.dateReglement && touched.dateReglement ? 'is-invalid' : ''}`} disabled={!canEdit} />
                            <ErrorMessage name="dateReglement" component="div" className="invalid-feedback" />
                        </FormGroup>
                    </Col>
                    <Col md={6}>
                        <FormGroup>
                            <Label>{t('reglement.montantRegle')} *</Label>
                            <Field name="montantRegle" type="number" className="form-control" value={values.montantRegle} disabled />
                        </FormGroup>
                    </Col>
                    <Col md={6}>
                        <FormGroup>
                            <Label>{t('reglement.modeReglement')} *</Label>
                            <Field as="select" name="modeReglement" className={`form-control ${errors.modeReglement && touched.modeReglement ? 'is-invalid' : ''}`} disabled={!canEdit || amountToPay === 0}>
                                {modeReglementOptions.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
                            </Field>
                            <ErrorMessage name="modeReglement" component="div" className="invalid-feedback" />
                        </FormGroup>
                    </Col>
                </Row>

                {values.modeReglement === 'CHEQUE' && amountToPay > 0 && (
                    <Row>
                        <Col md={4}><FormGroup><Label>{t('reglement.numeroCheque')}</Label><Field name="numeroCheque" className="form-control" disabled={!canEdit} /></FormGroup></Col>
                        <Col md={4}>
                            <FormGroup>
                                <Label>{t('reglement.banque')}</Label>
                                <Input type="select" name="banque" value={values.banque} onChange={handleChange} onBlur={handleBlur} disabled={!canEdit}>
                                    <option value="">{t('payment.selectBank', 'Sélectionner une banque')}</option>
                                    <option value="BMCE">BMCE</option>
                                    <option value="ATTIJARI">ATTIJARI</option>
                                    <option value="CIH">CIH</option>
                                    <option value="CREDIT DU MAROC">CREDIT DU MAROC</option>
                                    <option value="SG">SG</option>
                                </Input>
                            </FormGroup>
                        </Col>
                        <Col md={4}><FormGroup><Label>{t('reglement.dateEcheance')}</Label><Field name="dateEcheance" type="date" className="form-control" disabled={!canEdit} /></FormGroup></Col>
                    </Row>
                )}

                {values.modeReglement === 'VIREMENT' && amountToPay > 0 && (
                    <FormGroup>
                        <Label>{t('reglement.referencePaiement')}</Label>
                        <Field name="referencePaiement" className="form-control" disabled={!canEdit} />
                    </FormGroup>
                )}

                <Card className="mb-3 border-success">
                    <CardHeader className="bg-success bg-opacity-10 py-2">
                        <h6 className="mb-0 text-success">{t('reglement.retoursDisponibles', 'Avoirs retours disponibles')}</h6>
                    </CardHeader>
                    <CardBody>
                        {loadingRetours ? (
                            <div className="text-muted"><Spinner size="sm" className="me-2" />{t('common.loading', 'Chargement...')}</div>
                        ) : selectedRetours.length === 0 ? (
                            <Alert color="light" className="mb-0">{t('reglement.noRetoursDisponibles', 'Aucun avoir retour disponible ou applicable.')}</Alert>
                        ) : (
                            <Table bordered size="sm" className="mb-0">
                                <thead className="table-success">
                                    <tr>
                                        <th>{t('reglement.numero')}</th>
                                        <th>{t('reglement.montantDisponible', 'Disponible')}</th>
                                        <th>{t('reglement.montantAUtiliser', 'Utilisé')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {selectedRetours.map(retour => (
                                        <tr key={retour.retourId}>
                                            <td>{retour.numeroRetour}</td>
                                            <td>{formatPrice(retour.montantDisponible || retour.montantRetour)}</td>
                                            <td>
                                                <Input
                                                    type="number"
                                                    min="0"
                                                    step="0.01"
                                                    value={retour.montantUtilise}
                                                    disabled={!canEdit}
                                                    onWheel={preventScroll}
                                                    onChange={(e) => {
                                                        const next = selectedRetours.map(item => item.retourId === retour.retourId
                                                            ? { ...item, montantUtilise: Math.min(toNumber(e.target.value), toNumber(item.montantDisponible || item.montantRetour)) }
                                                            : item
                                                        ).filter(item => item.montantUtilise > 0);
                                                        const used = next.reduce((sum, item) => sum + toNumber(item.montantUtilise), 0);
                                                        setSelectedRetours(next);
                                                        setFieldValue('montantRetoursUtilises', used);
                                                        setFieldValue('montantPaiementReel', Math.max(selectedTotal - used, 0));
                                                    }}
                                                />
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        )}
                    </CardBody>
                </Card>

                <FormGroup>
                    <Label>{t('reglement.commentaire')}</Label>
                    <Field as="textarea" name="commentaire" rows="3" className="form-control" disabled={!canEdit} />
                </FormGroup>

                <div className="d-flex justify-content-end gap-2">
                    <Button type="button" color="secondary" outline onClick={() => navigate(-1)}>{t('common.cancel')}</Button>
                    {canEdit && (
                        <Button type="submit" color="primary" disabled={isSubmitting || loading || selectedDocuments.length === 0 || !values.fournisseurId}>
                            {(isSubmitting || loading) && <Spinner size="sm" className="me-2" />}
                            {reglement ? t('common.update') : t('common.create')}
                        </Button>
                    )}
                </div>
            </CardBody>
        </Card>
    );

    return (
        <Container fluid>
            <Formik innerRef={formikRef} initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit} enableReinitialize>
                {({ values, setFieldValue, isSubmitting, errors, touched, handleChange, handleBlur }) => (
                    <Form>
                        <Row className="g-3">
                            <Col lg={7}>{renderDocuments(values, setFieldValue)}</Col>
                            <Col lg={5}>{renderPayment(values, setFieldValue, isSubmitting, errors, touched, handleChange, handleBlur)}</Col>
                        </Row>
                    </Form>
                )}
            </Formik>
        </Container>
    );
};

export default ReglementFournisseurForm;
